import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertCircle,
  Download,
  FileText,
  MapPin,
  RefreshCw,
  TrendingUp,
  Users,
  DollarSign,
  Building2,
} from 'lucide-react';
import {
  fetchLeadershipReport,
  parseLeadershipReportResponse,
} from '@/services/leadershipReportAPI';
import fallbackReport from '@/report.json';
import {
  computePipelineReport,
  downloadCsv,
  formatCurrency,
  formatNumber,
  MIN_REPORT_LOAN_AMOUNT,
  normalizeApplication,
  parseReportPayload,
  recordsToCsvRows,
} from '@/utils/pipelineReport';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const CHART_COLORS = [
  '#047857',
  '#0d9488',
  '#0891b2',
  '#0284c7',
  '#6366f1',
  '#7c3aed',
  '#be185d',
  '#ea580c',
];

function chartPalette(labels) {
  return labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);
}

function leadershipRowsToRecords(rows) {
  return rows
    .map((r) =>
      normalizeApplication({
        loanApplicationId: r.id,
        submittedAt: r.submittedAt,
        loanAmount: r.loanAmountLabel,
        loanPurpose: r.loanPurpose,
        businessName: r.businessName,
        businessType: r.businessType,
        businessIndustry: r.businessIndustry,
        city: r.city,
        state: r.state,
        email: r.email,
        firstName: r.contactName?.split(' ')[0],
        lastName: r.contactName?.split(' ').slice(1).join(' '),
        ...r.raw,
      })
    )
    .filter(Boolean);
}

const PipelineImpactReport = () => {
  const reportRef = useRef(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [excludeSmallLoans, setExcludeSmallLoans] = useState(true);
  const [excludeEmptyAmount, setExcludeEmptyAmount] = useState(true);
  const [dedupeByName, setDedupeByName] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await fetchLeadershipReport({ pageNumber: 1, pageSize: 500 });
      const parsed = parseLeadershipReportResponse(json);
      const normalized = leadershipRowsToRecords(parsed.rows);
      if (normalized.length) {
        setRecords(normalized);
        setDataSource('Leadership API');
        return;
      }
      throw new Error('No records returned from API');
    } catch (apiErr) {
      try {
        const fallback = parseReportPayload(fallbackReport);
        setRecords(fallback);
        setDataSource('Local snapshot (report.json)');
        if (apiErr?.message && !apiErr.message.includes('not configured')) {
          setError(`API unavailable — showing local snapshot. (${apiErr.message})`);
        }
      } catch {
        setError(apiErr?.message || 'Could not load pipeline data.');
        setRecords([]);
        setDataSource('');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stateOptions = useMemo(() => {
    const set = new Set(records.map((r) => r.state).filter(Boolean));
    return Array.from(set).sort();
  }, [records]);

  const report = useMemo(
    () =>
      computePipelineReport(records, {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        state: stateFilter,
        excludeBelowMinLoan: excludeSmallLoans,
        excludeEmptyAmount,
        dedupeByName,
        minLoanAmount: MIN_REPORT_LOAN_AMOUNT,
      }),
    [records, startDate, endDate, stateFilter, excludeSmallLoans, excludeEmptyAmount, dedupeByName]
  );

  const stateChart = useMemo(() => {
    const labels = report.byStateAmount.slice(0, 8).map((x) => x.label);
    const values = report.byStateAmount.slice(0, 8).map((x) => x.total);
    return {
      labels,
      datasets: [
        {
          label: 'Requested capital',
          data: values,
          backgroundColor: chartPalette(labels),
        },
      ],
    };
  }, [report.byStateAmount]);

  const purposeChart = useMemo(() => {
    const labels = report.byPurpose.slice(0, 6).map((x) => x.label);
    const values = report.byPurpose.slice(0, 6).map((x) => x.count);
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: chartPalette(labels),
          borderWidth: 0,
        },
      ],
    };
  }, [report.byPurpose]);

  const handleCsvExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`halalcred-pipeline-report-${stamp}.csv`, recordsToCsvRows(report.records));
  };

  const handlePdfExport = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const margin = 48;
    let y = margin;
    const line = (text, size = 11, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(size);
      const lines = doc.splitTextToSize(String(text), 516);
      if (y + lines.length * (size + 4) > 760) {
        doc.addPage();
        y = margin;
      }
      doc.text(lines, margin, y);
      y += lines.length * (size + 4);
    };

    line('HalalCred Pipeline Impact Report', 18, true);
    line(`Generated ${new Date(report.generatedAt).toLocaleString()} · Source: ${dataSource}`, 10);
    line(
      `Period: ${report.period.start || '—'} to ${report.period.end || '—'} · Pipeline data only — not CRA exam submission.`,
      10
    );
    y += 8;

    line('Executive summary', 13, true);
    line(`Applications: ${report.summary.applicationCount}`);
    line(`Requested capital: ${formatCurrency(report.summary.requestedTotal)}`);
    line(`Average request: ${formatCurrency(report.summary.avgRequest)}`);
    line(`Faith-aligned pipeline businesses: ${report.summary.faithAlignedCount}`);
    line(`Likely LMI-area applications (city proxy): ${report.summary.likelyLmiCount}`);
    line(`Nonprofit applications: ${report.summary.nonprofitCount}`);
    y += 8;

    line('CRA-style buckets (application stage)', 13, true);
    line(
      `Revenue: ${report.byRevenueBucket.map((x) => `${x.label} (${x.count})`).join(' · ') || '—'}`
    );
    line(
      `Loan size: ${report.byLoanSizeBucket.map((x) => `${x.label} (${x.count})`).join(' · ') || '—'}`
    );
    y += 8;

    line('Modeled impact (illustrative)', 13, true);
    line(
      `Assumes ${Math.round(report.modeledImpact.conversionRate * 100)}% funding conversion, ${Math.round(report.modeledImpact.fundedRatio * 100)}% of requested amount.`
    );
    line(`Projected deployed capital: ${formatCurrency(report.modeledImpact.deployedCapital)}`);
    line(`Projected jobs supported: ~${formatNumber(report.modeledImpact.jobsSupported, 1)}`);
    y += 8;

    line('Top cities by application count', 13, true);
    report.cities.slice(0, 12).forEach((c) => {
      line(
        `${c.city}, ${c.state} — ${c.count} apps · ${formatCurrency(c.requestedTotal)} · ${c.lmiLabel}`
      );
    });

    const stamp = new Date().toISOString().slice(0, 10);
    doc.save(`halalcred-pipeline-report-${stamp}.pdf`);
  };

  return (
    <div className="space-y-6" ref={reportRef} id="pipeline-impact-report">
      <div className="rounded-xl border border-emerald-100 bg-gradient-to-r from-emerald-50/90 via-white to-teal-50/80 p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-emerald-800">
              <FileText className="h-5 w-5 shrink-0" />
              <span className="text-sm font-semibold uppercase tracking-wide">
                Pipeline impact report
              </span>
            </div>
            <h3 className="mt-1 text-lg font-semibold text-gray-900">
              Sharia-aligned financing demand from captured application data
            </h3>
            <p className="mt-1 max-w-3xl text-sm text-gray-600">
              Built from fields currently collected at intake: location, requested amount, industry,
              revenue band, and organization type. This is a pipeline report — not CRA exam-ready
              lending data.
            </p>
            {dataSource && (
              <p className="mt-2 text-xs text-emerald-800">Data source: {dataSource}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-emerald-200 text-emerald-800"
              onClick={load}
              disabled={loading}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-emerald-200 text-emerald-800"
              onClick={handleCsvExport}
              disabled={!report.records.length}
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-800"
              onClick={handlePdfExport}
              disabled={!report.records.length}
            >
              <FileText className="mr-2 h-4 w-4" />
              Export PDF
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="flex items-start gap-2 p-4 text-sm text-amber-900">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 p-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">From</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">To</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="min-w-[180px]">
            <label className="mb-1 block text-xs font-medium text-gray-600">State</label>
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All states" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                {stateOptions.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2 pb-2">
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={excludeEmptyAmount}
                onCheckedChange={(checked) => setExcludeEmptyAmount(checked === true)}
              />
              <span className="text-sm text-gray-700">Exclude empty or invalid amounts</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={excludeSmallLoans}
                onCheckedChange={(checked) => setExcludeSmallLoans(checked === true)}
              />
              <span className="text-sm text-gray-700">
                Exclude loans under {formatCurrency(MIN_REPORT_LOAN_AMOUNT)}
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={dedupeByName}
                onCheckedChange={(checked) => setDedupeByName(checked === true)}
              />
              <span className="text-sm text-gray-700">
                Remove duplicates (same email + business/contact name)
              </span>
            </label>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setStartDate('');
              setEndDate('');
              setStateFilter('all');
              setExcludeSmallLoans(true);
              setExcludeEmptyAmount(true);
              setDedupeByName(true);
            }}
          >
            Clear filters
          </Button>
        </CardContent>
      </Card>

      {report.filters.totalExcludedCount > 0 && (
        <p className="text-xs text-gray-500">
          Excluded from this report:
          {report.filters.excludedEmptyAmountCount > 0 && (
            <> {report.filters.excludedEmptyAmountCount} empty amount</>
          )}
          {report.filters.excludedSmallLoanCount > 0 && (
            <>
              {report.filters.excludedEmptyAmountCount > 0 ? ',' : ''}{' '}
              {report.filters.excludedSmallLoanCount} under{' '}
              {formatCurrency(MIN_REPORT_LOAN_AMOUNT)}
            </>
          )}
          {report.filters.excludedDuplicateCount > 0 && (
            <>
              {(report.filters.excludedEmptyAmountCount > 0 ||
                report.filters.excludedSmallLoanCount > 0) &&
                ','}{' '}
              {report.filters.excludedDuplicateCount} duplicate
              {report.filters.excludedDuplicateCount === 1 ? '' : 's'}
            </>
          )}
          .
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-emerald-100">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">Applications</p>
                <p className="mt-1 text-3xl font-bold">{report.summary.applicationCount}</p>
                <p className="mt-1 text-xs text-gray-500">
                  {report.summary.stateCount} states · {report.summary.cityCount} cities
                </p>
              </div>
              <Users className="h-8 w-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-100">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">Requested capital</p>
                <p className="mt-1 text-2xl font-bold">
                  {formatCurrency(report.summary.requestedTotal)}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Avg {formatCurrency(report.summary.avgRequest)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-100">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">Faith-aligned pipeline</p>
                <p className="mt-1 text-3xl font-bold">{report.summary.faithAlignedCount}</p>
                <p className="mt-1 text-xs text-gray-500">Muslim-market channel (all intake)</p>
              </div>
              <Building2 className="h-8 w-8 text-cyan-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-100">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">Likely LMI (city proxy)</p>
                <p className="mt-1 text-3xl font-bold">{report.summary.likelyLmiCount}</p>
                <p className="mt-1 text-xs text-gray-500">Not census-tract verified</p>
              </div>
              <MapPin className="h-8 w-8 text-emerald-700" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Requested capital by state</CardTitle>
          </CardHeader>
          <CardContent>
            {report.byStateAmount.length ? (
              <Bar
                data={stateChart}
                options={{
                  responsive: true,
                  plugins: { legend: { display: false } },
                  scales: {
                    y: {
                      ticks: {
                        callback: (v) => `$${Number(v).toLocaleString()}`,
                      },
                    },
                  },
                }}
              />
            ) : (
              <p className="text-sm text-gray-500">No data for selected filters.</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Applications by purpose</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center">
            {report.byPurpose.length ? (
              <div className="h-64 w-full max-w-sm">
                <Doughnut data={purposeChart} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            ) : (
              <p className="text-sm text-gray-500">No data for selected filters.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">CRA revenue buckets</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {report.byRevenueBucket.map((row) => (
              <div key={row.label} className="flex justify-between text-sm">
                <span className="text-gray-700">{row.label}</span>
                <span className="font-medium">{row.count}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">CRA loan size buckets</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {report.byLoanSizeBucket.map((row) => (
              <div key={row.label} className="flex justify-between text-sm">
                <span className="text-gray-700">{row.label}</span>
                <span className="font-medium">{row.count}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Organization type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {report.byOrgType.map((row) => (
              <div key={row.label} className="flex justify-between text-sm">
                <span className="text-gray-700">{row.label}</span>
                <span className="font-medium">{row.count}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="border-teal-100 bg-teal-50/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-5 w-5 text-teal-700" />
            Modeled deployed capital & jobs (illustrative)
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div>
            <p className="text-sm text-gray-600">Projected deployed</p>
            <p className="text-xl font-bold text-gray-900">
              {formatCurrency(report.modeledImpact.deployedCapital)}
            </p>
            <p className="text-xs text-gray-500">
              {Math.round(report.modeledImpact.conversionRate * 100)}% conversion ×{' '}
              {Math.round(report.modeledImpact.fundedRatio * 100)}% of requested
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Projected jobs supported</p>
            <p className="text-xl font-bold text-gray-900">
              ~{formatNumber(report.modeledImpact.jobsSupported, 1)}
            </p>
            <p className="text-xs text-gray-500">
              ~{report.modeledImpact.jobsPer100k} jobs per $100K deployed
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Top industries</p>
            <p className="text-sm font-medium text-gray-900">
              {report.byIndustry
                .slice(0, 3)
                .map((x) => x.label)
                .join(' · ') || '—'}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Geography by city</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b text-gray-600">
                <th className="px-3 py-2 font-medium">City</th>
                <th className="px-3 py-2 font-medium">State</th>
                <th className="px-3 py-2 font-medium">Apps</th>
                <th className="px-3 py-2 font-medium">Requested</th>
                <th className="px-3 py-2 font-medium">ZIPs</th>
                <th className="px-3 py-2 font-medium">LMI proxy</th>
              </tr>
            </thead>
            <tbody>
              {report.cities.map((row) => (
                <tr key={`${row.city}-${row.state}`} className="border-b border-gray-100">
                  <td className="px-3 py-2">{row.city}</td>
                  <td className="px-3 py-2">{row.state}</td>
                  <td className="px-3 py-2">{row.count}</td>
                  <td className="px-3 py-2">{formatCurrency(row.requestedTotal)}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{row.zips || '—'}</td>
                  <td className="px-3 py-2">{row.lmiLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Application detail</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b text-gray-600">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Business</th>
                <th className="px-3 py-2 font-medium">Location</th>
                <th className="px-3 py-2 font-medium">Requested</th>
                <th className="px-3 py-2 font-medium">Purpose</th>
                <th className="px-3 py-2 font-medium">Industry</th>
                <th className="px-3 py-2 font-medium">Revenue bucket</th>
              </tr>
            </thead>
            <tbody>
              {report.records.map((row) => (
                <tr key={row.id} className="border-b border-gray-100">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.submittedAt ? row.submittedAt.slice(0, 10) : '—'}
                  </td>
                  <td className="px-3 py-2">{row.businessName}</td>
                  <td className="px-3 py-2">
                    {row.city}, {row.state} {row.zipCode}
                  </td>
                  <td className="px-3 py-2">{formatCurrency(row.loanAmountNum)}</td>
                  <td className="px-3 py-2">{row.loanPurpose}</td>
                  <td className="px-3 py-2">{row.businessIndustry}</td>
                  <td className="px-3 py-2">{row.revenueBucket}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="text-xs text-gray-500">
        Report generated {new Date(report.generatedAt).toLocaleString()}. Pipeline metrics use
        application intake data only. LMI labels are city-level proxies. Modeled deployed capital
        and jobs are illustrative projections, not audited outcomes.
      </p>
    </div>
  );
};

export default PipelineImpactReport;
